import { getFormattedDate, numberFormatter } from './utilities';
import { IBatchesData, ISender } from '../interfaces/funds';
import { checkRoles, CLAIMS } from './roles-helper';
import React, { FC, Fragment } from 'react';
import { IAuthState } from '../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../setup';
import { Title } from '../components/developers/page-components/Title';
import { RelatedEntityNameLink } from '../components/merchants/RelatedEntityNameLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

export const getCaptureDate = (capturePeriodFrom: string, capturePeriodTo: string): JSX.Element => {
  return (
    <div className="fs-8">
        <span>
          ({getFormattedDate(capturePeriodFrom, 'dd MMM, yyyy')} - {' '}
        </span>
        <span>
          {getFormattedDate(capturePeriodTo, 'dd MMM, yyyy')})
        </span>
  </div>
  );
};

export const getValueMark = (fieldName: string, value: number | string): string => {
  // Skip the mark for string values or specific fields
  if (typeof value === 'string' || value === 0 || !value || fieldName === 'payments' || fieldName === 'fundingChannel') {
    return '';
  } else if (value > 0) {
    return '+';
  } else {
    return '-';
  }
};

export const getValue = (value: number | string): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (!value) {
    return numberFormatter(0);
  }
  if (value >= 0) {
    return numberFormatter(value);
  } else {
    return numberFormatter(value * -1);
  }
};

export const getTotalAmount = (totalAmount: number, currencySymbol: string):string => {
  if (totalAmount < 0) {
    return `-${currencySymbol}${numberFormatter(totalAmount*-1)}`;
  } else {
    return `${currencySymbol}${numberFormatter(totalAmount)}`;
  }
};

export const getRelatedTypes = (sender: ISender, receiver: ISender): { name: string; id: string; title: string; type: string; claim: CLAIMS }[] => [
  {
    title: 'Sender',
    type: sender?.relatedEntityType,
    id: sender?.relatedEntityId,
    name: sender?.relatedEntityDba,
    claim: CLAIMS.SUPER_ADMIN,
  },
  {
    title: 'Receiver',
    type: receiver?.relatedEntityType,
    id: receiver?.relatedEntityId,
    name: receiver?.relatedEntityDba,
    claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
  },
];


interface IFieldsProps {
  fieldName: string;
  title: string;
  count?: number;
  value: number | string; // Update to accept string as well
}

export const detailsInformationFields = (data: IBatchesData): IFieldsProps[] => [
  // {
  //   fieldName: 'fundingChannel',
  //   title: 'funding channel',
  //   value: data?.fundingChannel || '-'
  // },
  {
    fieldName: 'payments',
    title: 'payments',
    count: data?.totalCount,
    value: data?.totalValue
  },
  {
    fieldName: 'returns',
    title: 'returns',
    count: data?.returnsCount,
    value: data?.returns
  },
  {
    fieldName: 'chargebacks',
    title: 'chargeback value',
    value: data?.chargebacks
  },
  {
    fieldName: 'flexChargeFees',
    title: 'processing fees',
    count: data?.flexChargeFeesCount,
    value: data?.flexChargeFees
  },
  {
    fieldName: 'flexChargeChargebackFeesAmount',
    title: 'chargeback fees',
    count: data?.chargebacksCount,
    value: data?.flexChargeChargebackFeesAmount
  },
  {
    fieldName: 'adjustment',
    title: 'adjustment',
    value: data?.adjustment
  },
  {
    fieldName: 'reserveHold',
    title: 'reserve hold',
    value: data?.reserveHold
  },
  {
    fieldName: 'reserveRelease',
    title: 'reserve release',
    value: data?.reserveRelease
  },
  {
    fieldName: 'reserveUtilization',
    title: 'reserve utilization',
    value: data?.reserveUtilization
  },

];

interface Props {
  sender: ISender;
  receiver: ISender;
}

export const DetailsRelatedTypes: FC<Props> = ({ sender, receiver }) => {
  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

  return (
    <>
      {getRelatedTypes(sender, receiver)?.map(({ title, id, name, type, claim }) => (
        <Fragment key={title}>
          {
            checkRoles(authUser, claim) && (
              <>
                <div>
                  <Title title={title} additionalClass="fs-6 fw-bolder" />
                  <RelatedEntityNameLink name={name} id={id} type={type} />
                </div>
                {title === 'Sender' && (
                  <FontAwesomeIcon icon={faArrowRight} width={12} className='mx-5'/>
                )}
              </>
            )
          }
        </Fragment>
      ))}
    </>
  );
};
